<template>
  <v-card class="edit-invoice" height="90vh">
    <v-card-title>{{ $t('admin_acc_invoice.edit_title') }}</v-card-title>
    <v-card-text>
      <div class="edit-invoice-content" v-if="purchaseOrder">
        <div class="edit-invoice-content-col left">
          <v-form ref="form">
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.id')"
                          :value="`PO${purchaseOrder.id.toString().padStart(10, '0')}`"></v-text-field>
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.recipient')"
                          :value="$t('name', {given_name: purchaseOrder.recipient.given_name, surname: purchaseOrder.recipient.surname})"></v-text-field>
            <v-text-field readonly
                          :label="$t('admin_acc_invoice.status')"
                          :value="$t(`invoice_status.${invoice.status}`)">
              <template #append >
                <v-tooltip top
                           v-if="invoice.pdf">
                  <template #activator="{on, attrs}">
                    <a :href="'api/static/'+invoice.pdf"
                       target="_blank">
                      <v-btn icon
                             v-on="on"
                             v-bind="attrs">
                        <v-icon>mdi-download</v-icon>
                      </v-btn>
                    </a>
                  </template>
                  <span>INVOICE</span>
                </v-tooltip>
                <v-tooltip top
                           v-if="invoice.receipt">
                  <template #activator="{on, attrs}">
                    <a :href="'api/static/'+invoice.receipt"
                       target="_blank">
                      <v-btn icon
                             v-on="on"
                             v-bind="attrs">
                        <v-icon>mdi-receipt-text</v-icon>
                      </v-btn>
                    </a>
                  </template>
                  <span>RECEIPT</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <v-input :rules="requiredRules"
                     v-model="invoice.issued_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_invoice.issued_at') }}</legend>
                <DatePickerInput :readonly="invoice.status === 'SETTLED'"
                                 v-model="invoice.issued_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>
            <v-input :rules="requiredRules"
                     v-model="invoice.due_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_invoice.due_at') }}</legend>
                <DatePickerInput :readonly="invoice.status === 'SETTLED'"
                                 v-model="invoice.due_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>
            <v-text-field :outlined="invoice.status !== 'SETTLED'"
                          :readonly="invoice.status === 'SETTLED'"
                          :label="$t('admin_acc_invoice.title')"
                          v-model="invoice.title"
                          :rules="requiredRules"></v-text-field>
            <v-text-field readonly
                          :label="$t('admin_acc_invoice.amount')"
                          :value="invoice.amount / 100"></v-text-field>
            <v-textarea outlined
                        auto-grow
                        no-resize
                        :label="$t('admin_acc_invoice.remarks')"
                        v-model="invoice.remarks"></v-textarea>
          </v-form>
        </div>
        <div class="edit-invoice-content-col right">
          <v-tabs v-model="tabIndex">
            <v-tab>{{ $t('admin_acc_invoice.bookings') }}</v-tab>
            <v-tab-item>
              <AdminBookingList full-height
                                hide-actions
                                :show-select="invoice.status !== 'SETTLED'"
                                v-model="selectedBookingIds"
                                :extra-filters="{'purchase_order.id': purchaseOrder.id}"></AdminBookingList>
            </v-tab-item>

            <v-tab>{{ $t('admin_acc_invoice.item') }}</v-tab>
            <v-tab-item>
              <InvoiceItemEditor :readonly="invoice.status === 'SETTLED'"
                                 v-model="invoice.items"
                                 :purchase-order-id="purchaseOrder.id"
                                 :booking-ids="selectedBookingIds"></InvoiceItemEditor>
              <div class="invoice-items-summary mt-2">
                <div class="invoice-discount">
                  <v-text-field :outlined="invoice.status !== 'SETTLED'"
                                :readonly="invoice.status === 'SETTLED'"
                                :label="$t('admin_acc_invoice.discount_percentage')"
                                suffix="%"
                                type="number"
                                step=".01"
                                min="0"
                                v-model="invoice.discount_percentage"></v-text-field>
                  <v-text-field readonly
                                :label="$t('admin_acc_invoice.discount_amount')"
                                prefix="-"
                                :value="discountAmount"></v-text-field>
                </div>
                <div class="invoice-total">
                  <v-text-field readonly
                                :label="$t('admin_acc_invoice.total')"
                                :value="invoiceTotalAfterDiscount"></v-text-field>
                </div>
              </div>
            </v-tab-item>

            <v-tab>{{ $t('admin_acc_invoice.payment') }}</v-tab>
            <v-tab-item>
              <v-form ref="paymentForm">
                <v-input>
                  <v-container fluid class="flex-column pa-0">
                    <legend class="v-label theme--light">{{ $t('admin_acc_invoice.payment_method') }}</legend>
                    <v-radio-group v-model="invoice.payment_method"
                                   :readonly="invoice.status === 'SETTLED'"
                                   :rules="requiredRules">
                      <v-radio :label="$t('payment_method.bank_transfer')"
                               value="bank_transfer"></v-radio>
                      <v-radio :label="$t('payment_method.cheque')"
                               value="cheque"></v-radio>
                      <v-radio :label="$t('payment_method.cyber_source')"
                               disabled
                               value="cyber_source"></v-radio>
                    </v-radio-group>
                  </v-container>
                </v-input>
                <v-input v-if="invoice.payment_method !== 'cyber_source'"
                         v-model="invoice.payment_proof"
                         :rules="requiredRules">
                  <v-container fluid class="flex-column pa-0">
                    <legend class="v-label theme--light">{{ $t('admin_acc_invoice.payment_proof') }}</legend>
                    <PhotoPreviewInput :readonly="invoice.status === 'SETTLED'"
                                       v-model="invoice.payment_proof"
                                       :div-class="invoice.payment_method"
                                       file-name="payment_proof"></PhotoPreviewInput>
                  </v-container>
                </v-input>
              </v-form>
            </v-tab-item>
          </v-tabs>
        </div>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-btn color="primary"
             @click="$emit('submit')">
        {{ $t('close') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary"
             v-if="tabIndex === 0"
             :disabled="invoice.status === 'SETTLED'"
             @click="$refs.form.validate() && send()">
        {{ $t('admin_acc_invoice.send') }}
      </v-btn>
      <v-btn color="primary"
             v-if="tabIndex === 1"
             @click="$refs.form.validate() && save()">
        {{ invoice.status === 'SETTLED' ? $t('admin_acc_invoice.save_remarks') : $t('save') }}
      </v-btn>

      <v-btn color="primary"
             v-if="tabIndex === 2 && invoice.status !== 'SETTLED'"
             :disabled="invoice.status === 'SETTLED'"
             @click="$refs.paymentForm.validate() && uploadProof()">
        {{ $t('save') }}
      </v-btn>
      <v-btn color="primary"
             v-if="tabIndex === 2 && invoice.status === 'PENDING_CONFIRMATION'"
             :disabled="invoice.status === 'SETTLED'"
             @click="approve">
        {{ $t('admin_acc_invoice.approve') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import {computed, onMounted, ref} from "@vue/composition-api";
import DatePickerInput from "@/components/DatePickerInput.vue";
import InvoiceItemEditor from "@/components/acc_booking/invoice/InvoiceItemEditor.vue";
import PhotoPreviewInput from "@/components/PhotoPreviewInput.vue";
import AdminBookingList from "@/components/acc_booking/booking/AdminBookingList.vue";

export default {
  name: 'EditInvoiceModal',
  components: {AdminBookingList, PhotoPreviewInput, InvoiceItemEditor, DatePickerInput},
  props: {
    invoiceId: String,
  },
  setup(props, {root, emit}) {
    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);
    const invoice = ref(null);
    const purchaseOrder = ref(null);
    const getInvoice = function () {
      root.$callAPI('aafb/admin/invoice/', {
        invoice_id: props.invoiceId,
      }, (response) => {
        invoice.value = {
          ...response.body.invoice,
          ...JSON.parse(response.body.invoice.details),
          issued_at: root.$parseDate(response.body.invoice.issued_at, 'yyyy-MM-dd'),
          due_at: root.$parseDate(response.body.invoice.due_at, 'yyyy-MM-dd'),
        };

        purchaseOrder.value = {
          ...response.body.invoice.purchase_order,
          issued_at: root.$parseDate(response.body.invoice.purchase_order.issued_at, 'yyyy-MM-dd'),
          due_at: root.$parseDate(response.body.invoice.purchase_order.due_at, 'yyyy-MM-dd'),
        };

        selectedBookingIds.value = invoice.value.booking_ids || [];
      });
    }

    const tabIndex = ref(0);

    const discountAmount = computed(() => {
      if (invoice.value.discount_percentage) {
        let p = parseFloat(invoice.value.discount_percentage) || 0;
        return (invoiceTotal.value * p / 100).toFixed(2);
      }
      return 0;
    })
    const invoiceTotal = computed(() => {
      if (invoice.value.items) {
        let s = 0;

        _.forEach(invoice.value.items, (item) => {
          let unitPrice = parseFloat(item.unit_price) || 0;
          let quantity = parseInt(item.quantity) || 1;

          s += unitPrice * quantity;
        });
        return s.toFixed(2);
      }

      return 0;
    });
    const invoiceTotalAfterDiscount = computed(() => {
      return (invoiceTotal.value - discountAmount.value).toFixed(2);
    })

    const selectedBookingIds = ref([]);

    const send = function () {
      if (confirm(root.$t('admin_acc_invoice.send_confirm_msg'))) {
        let details = {
          items: invoice.value.items,
          discount_percentage: invoice.value.discount_percentage,
          booking_ids: selectedBookingIds.value,
        }

        root.$callAPI('aafb/admin/invoice/edit/', {
          invoice_id: props.invoiceId,
          ...invoice.value,
          details: JSON.stringify(details),
        }, function (response) {
          root.$callAPI('aafb/admin/invoice/send/', {
            invoice_id: props.invoiceId,
          }, function (response) {
            root.$showAlertMessage(root.$t('admin_acc_invoice.send_success_msg'));
            emit('submit', true);
          })
        })
      }
    }
    const save = function () {
      let details = {
        items: invoice.value.items,
        discount_percentage: invoice.value.discount_percentage,
      }

      root.$callAPI('aafb/admin/invoice/edit/', {
        invoice_id: props.invoiceId,
        ...invoice.value,
        details: JSON.stringify(details),
      }, function () {
        emit('submit', true);
      })
    }

    const uploadProof = async function () {
      root.$callAPI('aafb/admin/invoice/upload_proof/', {
        invoice_id: invoice.value.id,
        payment_method: invoice.value.payment_method,
        payment_proof: invoice.value.payment_proof instanceof File ? await root.$convertFileToBase64(invoice.value.payment_proof) : invoice.value.payment_proof,
      }, (response) => {
        root.$showAlertMessage(root.$t('invoice.upload_success'));
        getInvoice();
      })
    }

    const approve = function () {
      if (confirm(root.$t('admin_acc_invoice.approve_confirm_msg'))) {
        root.$callAPI('aafb/admin/invoice/approve/', {
          invoice_id: props.invoiceId,
        }, function (response) {
          emit('submit', true);
        })
      }
    }

    onMounted(() => {
      getInvoice()
    })

    return {
      requiredRules,
      invoice,
      purchaseOrder,

      tabIndex,

      discountAmount,
      invoiceTotalAfterDiscount,

      selectedBookingIds,

      send,
      save,
      uploadProof,
      approve,
    }
  }
}
</script>

<style lang="less">
.edit-invoice {
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .v-card__title, .v-card__text, .v-card__actions {
    width: 100%;
  }

  .v-card__title, .v-card__actions {
    flex: 0 0 auto;
  }

  .v-card__text {
    flex: 1 1 0;
    overflow: auto;
  }

  .edit-invoice-content {
    width: 100%;
    height: 100%;

    display: flex;
    gap: 12px;

    .edit-invoice-content-col {
      height: 100%;

      display: flex;
      flex-direction: column;
      overflow-y: auto;

      &.left {
        flex: 0 0 300px;
      }

      &.right {
        flex: 1 1 0;
      }
    }
  }

  .invoice-items-summary {
    flex: 0 0 auto;

    .invoice-discount, .invoice-total {
      display: flex;
      gap: 12px;
    }
  }

  .v-tabs {
    height: 100%;

    .v-tabs-bar {
      flex: 0 0 auto;
    }

    .v-tabs-items {
      height: calc(100% - 48px);
      flex: 1 1 0;

      .v-window__container {
        height: 100% !important;

        .v-window-item {
          height: 100% !important;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          padding-top: 12px;
        }
      }
    }
  }
}


</style>
